import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import {
  ArrowLeft,
  Plus,
  PencilLine,
  Trash2,
  Eye,
  Send,
  Activity,
  Search,
  Check,
  History,
  ArrowUpDown,
} from "lucide-react";
import "./FlangeHistory.css";

const ACTION_TONES = {
  blinded: { bg: "#fff7df", color: "#8a5b0a", border: "#ead7a1", Icon: Eye },
  deblinded: { bg: "#eaf3f7", color: "#27647a", border: "#bdd6df", Icon: PencilLine },
  tightened: { bg: "#eaf5ee", color: "#287147", border: "#bddac8", Icon: Check },
  create: { bg: "#eef2f4", color: "#52616b", border: "#d5dde1", Icon: Plus },
  edit: { bg: "#eaf3f7", color: "#27647a", border: "#bdd6df", Icon: PencilLine },
  update: { bg: "#eaf3f7", color: "#27647a", border: "#bdd6df", Icon: PencilLine },
  modify: { bg: "#eaf3f7", color: "#27647a", border: "#bdd6df", Icon: PencilLine },
  submit: { bg: "#fff7df", color: "#8a5b0a", border: "#ead7a1", Icon: Send },
  view: { bg: "#eef2f4", color: "#52616b", border: "#d5dde1", Icon: Eye },
  delete: { bg: "#faecea", color: "#9d2e25", border: "#e8c4c0", Icon: Trash2 },
};

const DEFAULT_TONE = { bg: "#eef2f4", color: "#52616b", border: "#d5dde1", Icon: Activity };

const getActionTone = (log) => {
  const context = [log?.action, log?.form_created_or_changed, log?.description]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (context.includes("deblind")) return { ...ACTION_TONES.deblinded, label: "Deblinded" };
  if (context.includes("tighten")) return { ...ACTION_TONES.tightened, label: "Tightened" };
  if (context.includes("blind")) return { ...ACTION_TONES.blinded, label: "Blinded" };
  const key = log?.action?.toLowerCase();
  return { ...(ACTION_TONES[key] || DEFAULT_TONE), label: log?.action || "Activity" };
};

const getInitials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

export default function Logs({ isSidebarExpanded }) {
  const { flange_id } = useParams();
  const [logs, setLogs] = useState([]);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [selectedSerial, setSelectedSerial] = useState(flange_id);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  const flangesListRef = useRef(null);
  const flangeItemRefs = useRef({});

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem("selectedProject");
      const updated = stored ? JSON.parse(stored) : null;
      setSelectedProject(updated);
    };

    handleStorageChange();
    window.addEventListener("projectChanged", handleStorageChange);

    return () => {
      window.removeEventListener("projectChanged", handleStorageChange);
    };
  }, []);

  // Auto-scroll to selected flange when it is out of view
  useEffect(() => {
    const selectedElement = flangeItemRefs.current[selectedSerial];
    const container = flangesListRef.current;
    if (!selectedSerial || !selectedElement || !container) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = selectedElement.getBoundingClientRect();

    if (
      elementRect.top < containerRect.top ||
      elementRect.bottom > containerRect.bottom
    ) {
      container.scrollTo({
        top: selectedElement.offsetTop - container.clientHeight / 2,
        behavior: "smooth",
      });
    }
  }, [selectedSerial, serialNumbers]);

  const fetchSerialNumbers = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/get_all_flanges`, {
        params: { project_selected: selectedProject },
      });
      if (response.status === 200) {
        setSerialNumbers(response.data.data || []);
      } else {
        setErrorMessage("Failed to fetch serial numbers.");
      }
    } catch (error) {
      console.error("Error fetching serial numbers:", error);
      setErrorMessage("Error fetching serial numbers.");
    }
  };

  const fetchLogs = async (flangeId) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await axiosInstance.get(`${API_URL}/api/logs/${flangeId}`);

      if (response.status === 200) {
        setLogs(response.data.result || []);
      } else {
        setLogs([]);
        setErrorMessage("Failed to fetch logs.");
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
      if (error.response) {
        setErrorMessage(
          `Error fetching logs: ${error.response.status} - ${
            error.response.data?.message || "Unknown error"
          }`
        );
      } else if (error.request) {
        setErrorMessage("Network error: Unable to reach server.");
      } else {
        setErrorMessage("Error fetching logs: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      fetchSerialNumbers();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedSerial) {
      fetchLogs(selectedSerial);
    }
  }, [selectedSerial]);

  const handleBack = () => {
    window.history.back();
  };

  const selectedFlangeInfo = serialNumbers.find(
    (flange) => flange.flange_id === selectedSerial
  );

  const filteredFlanges = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return serialNumbers;
    return serialNumbers.filter((flange) =>
      (flange.flange_number || "").toLowerCase().includes(term) ||
      String(flange.serial_number || "").toLowerCase().includes(term)
    );
  }, [serialNumbers, search]);

  const sortedLogs = useMemo(() => {
    const getTimestamp = (log) => {
      const raw = log?.done_at || log?.time_acc_to_timezone;
      const timestamp = Date.parse(raw);
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };
    return [...logs].sort((a, b) =>
      sortOrder === "newest"
        ? getTimestamp(b) - getTimestamp(a)
        : getTimestamp(a) - getTimestamp(b)
    );
  }, [logs, sortOrder]);

  return (
    <div
      className={`table-container flange-history-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="flange-history-content">
        <div className="flange-history-header">
          <button type="button" className="flange-history-back" onClick={handleBack}>
            <ArrowLeft size={14} />
            Back
          </button>
          <span className="flange-history-header-divider" />
          <h2>Flange Activity</h2>
          {selectedFlangeInfo?.flange_number && (
            <span className="flange-history-badge-mono">
              {selectedFlangeInfo.flange_number}
            </span>
          )}
        </div>

        {errorMessage && (
          <div className="flange-history-error">{errorMessage}</div>
        )}

        <div className="flange-history-layout">
          {/* Flange list */}
          <div className="flange-history-list-panel">
            <div className="flange-history-list-header">
              <span>Flanges</span>
              <span>{filteredFlanges.length}</span>
            </div>
            <label className="flange-history-search-wrap">
              <Search size={14} aria-hidden="true" />
              <input
                className="flange-history-search"
                placeholder="Search flange ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search flange ID"
              />
            </label>
            <div className="flange-history-list" ref={flangesListRef}>
              {filteredFlanges.length === 0 ? (
                <div className="flange-history-list-empty">
                  No flanges match "{search}".
                </div>
              ) : (
                filteredFlanges.map((flange, index) => (
                  <button
                    type="button"
                    key={flange.flange_id}
                    ref={(el) => {
                      if (el) flangeItemRefs.current[flange.flange_id] = el;
                    }}
                    className={`flange-history-list-item ${
                      selectedSerial === flange.flange_id ? "is-selected" : ""
                    }`}
                    onClick={() => setSelectedSerial(flange.flange_id)}
                    title={flange.flange_number || "Unknown"}
                  >
                    <span className="flange-history-list-serial">
                      {flange.serial_number ?? index + 1}
                    </span>
                    <span className="flange-history-list-number">
                      {flange.flange_number || "Unknown"}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="flange-history-main">
            <div className="flange-history-main-header">
              <span className="flange-history-main-title">Activity Timeline</span>
              {!loading && logs.length > 0 && (
                <span className="flange-history-main-sub">
                  {logs.length} {logs.length === 1 ? "entry" : "entries"}
                </span>
              )}
              {!loading && logs.length > 1 && (
                <label className="flange-history-sort">
                  <ArrowUpDown size={13} aria-hidden="true" />
                  <span>Time</span>
                  <select
                    value={sortOrder}
                    onChange={(event) => setSortOrder(event.target.value)}
                    aria-label="Sort activity by time"
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </label>
              )}
            </div>

            {loading ? (
              <div className="flange-history-state">
                <div className="flange-history-spinner" />
                <span className="flange-history-state-sub">Loading activity…</span>
              </div>
            ) : logs.length > 0 ? (
              <div className="flange-history-scroll">
                <div className="flange-history-timeline">
                  {sortedLogs.map((log, index) => {
                    const tone = getActionTone(log);
                    const { Icon } = tone;
                    const isLast = index === sortedLogs.length - 1;

                    return (
                      <div
                        key={`${log.done_at}-${index}`}
                        className={`flange-history-timeline-row ${
                          isLast ? "is-last" : ""
                        }`}
                      >
                        <span className="flange-history-timeline-marker" style={{ borderColor: tone.color }} />

                        <div className="flange-history-timeline-content">
                          <div className="flange-history-timeline-line1">
                            <span
                              className="flange-history-action-pill"
                              style={{ background: tone.bg, color: tone.color, borderColor: tone.border }}
                            >
                              {tone.label}
                            </span>
                            {log.form_created_or_changed && (
                              <span className="flange-history-form-name">
                                {log.form_created_or_changed}
                              </span>
                            )}
                          </div>

                          <div className="flange-history-timeline-time">
                            {log.time_acc_to_timezone || "Timestamp unavailable"}
                          </div>

                          {log.description && (
                            <div className="flange-history-timeline-desc">
                              {log.description}
                            </div>
                          )}

                          <div className="flange-history-timeline-user">
                            <span className="flange-history-avatar">
                              {getInitials(log.done_by_user)}
                              <span className="flange-history-avatar-badge" style={{ background: tone.color }}>
                                <Icon size={8} strokeWidth={2.5} />
                              </span>
                            </span>
                            <span className="flange-history-user-name">
                              {log.done_by_user || "Unknown User"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                 
                </div>
              </div>
            ) : (
              <div className="flange-history-state">
                <Activity size={26} color="#9ca3af" />
                <span className="flange-history-state-title">
                  No activity found
                </span>
                <span className="flange-history-state-sub">
                  This flange doesn't have any recorded activity yet.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
